import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { notification } from 'antd';

import { assetKeys, glossaryKeys } from '@/shared/lib/query/queryKeys';

import type { AssetBaseInfo } from '@/features/asset-detail/api';
import {
  fetchGlossaryTree,
  generateGlossaryTermPatchOperations,
  updateAssetGlossaryTerms,
} from '@/features/asset-detail/api/glossaryApi';
import { GLOSSARY_CONFIG } from '@/features/asset-detail/lib/constants';

export const useGlossaryTree = () => {
  return useQuery({
    queryKey: glossaryKeys.glossaryTree,
    queryFn: fetchGlossaryTree,
    staleTime: GLOSSARY_CONFIG.QUERY.STALE_TIME,
  });
};

type UpdateGlossaryTermsParams = {
  entityType: string;
  entityId: string;
  fqn: string;
  newTermFQNs: string[];
  glossaryTermsMap?: Map<string, { description: string; displayName: string }>;
};

export const useUpdateAssetGlossaryTerms = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ entityType, entityId, fqn, newTermFQNs, glossaryTermsMap }: UpdateGlossaryTermsParams) => {
      const currentData = queryClient.getQueryData<AssetBaseInfo>(assetKeys.assetBaseInfo(entityType, fqn));
      const oldTags = currentData?.tags || [];

      const patchOperations = generateGlossaryTermPatchOperations(oldTags, newTermFQNs, glossaryTermsMap);

      if (patchOperations.length === 0) {
        return;
      }

      await updateAssetGlossaryTerms(entityType, entityId, patchOperations);
    },
    onError: (error) => {
      const errorMessage = error instanceof Error ? error.message : '용어 업데이트 중 오류가 발생했습니다.';

      notification.error({
        message: '용어 업데이트 실패',
        description: errorMessage,
        placement: 'bottomLeft',
        duration: 5,
      });
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: assetKeys.assetBaseInfo(variables.entityType, variables.fqn),
      });

      queryClient.invalidateQueries({ queryKey: glossaryKeys.taggedAssetsPrefix() });
      queryClient.invalidateQueries({ queryKey: glossaryKeys.taggedAssetsCountPrefix() });

      notification.success({
        message: '용어 업데이트 완료',
        description: '용어가 성공적으로 업데이트되었습니다.',
        placement: 'bottomLeft',
        duration: 3,
      });
    },
  });
};
